import { useMemo } from "react"

import { useAPIHealth } from "shared/api/health/useAPIHealth"
import { useEvent } from "shared/lib/useEvent"
import { RoomTypename } from "shared/types/Project"
import { Room } from "shared/types/Room"

import { useUpdateAttachmentLinks } from "entities/attachments/api/useUpdateAttachmentLinks"
import { getProductTotal } from "entities/projects/lib/getProductTotal"
import { useProjectChildPosition } from "entities/projects/model/useProjectChildPosition"
import { useProjectRequestMonitor } from "entities/projects/model/useProjectRequestState"
import { useRoomMutations } from "entities/rooms/model/useRoomMutations"
import { useProjectStore } from "features/project-management/model/useProjectStore"
import { useRoom } from "features/room-management/model/useRoom"
import { RoomFormData } from "features/room-management/types/RoomFormData"
import { useRoomForm } from "widgets/room/room-form/model/useRoomForm"

import { ProjectPageRoomSidebarModel } from "../ProjectPageRoomSidebar"

export interface UseProjectPageRoomSidebarModelArgs {
  projectID: string
  room: Room
}

export function useProjectPageRoomSidebarModel({
  projectID,
  room
}: UseProjectPageRoomSidebarModelArgs): ProjectPageRoomSidebarModel {
  const {
    pendingElementIDs,
    sidebarIsEditing,
    sidebarActiveTab,
    enterSidebarEditing,
    exitSidebarEditing,
    setSidebarActiveTab
  } = useProjectStore(projectID)
  const isPending = pendingElementIDs.includes(room.id)

  const { price: isPriceServiceAvailable } = useAPIHealth()

  const { isUpdatingChildren } = useProjectRequestMonitor({ projectID })

  const { updateRoom } = useRoomMutations()
  const updateAttachmentLinks = useUpdateAttachmentLinks()

  const roomModel = useRoom({
    roomID: room.id,
    projectID,
    options: { includePrice: true }
  })

  const { parent, displayPosition } = useProjectChildPosition({
    child: room,
    projectID
  })

  const handleSubmit = useEvent(async (roomFormData: RoomFormData) => {
    roomModel.clarificationRequest?.handlers.onSubmitContent?.(
      roomFormData.clarificationRequest?.content
    )
    const results = await Promise.all([
      updateRoom({
        ...roomFormData,
        room: {
          ...roomFormData.room,
          id: room.id
        }
      }),
      roomFormData.attachments
        ? updateAttachmentLinks({
            id: room.id,
            resourceType: RoomTypename,
            attachmentLinks: roomFormData.attachments
          })
        : Promise.resolve({ data: {}, errors: undefined })
    ])
    const errors = results.flatMap(r => r?.errors ?? [])
    return { data: {}, errors: errors.length ? errors : undefined }
  })

  const roomFormModel = useRoomForm({
    room,
    projectID,
    parentID: parent?.id,
    displayPosition,
    clarificationRequest: roomModel.clarificationRequest,
    isPending,
    onSubmit: handleSubmit
  })

  const productTotal = useMemo(
    () => getProductTotal(room.children),
    [room.children]
  )

  const { data: priceData, isLoading: isLoadingPrice = false } =
    roomModel.pricing.data.price ?? {}

  return {
    projectID,
    room,
    price: isPriceServiceAvailable ? priceData : null,
    isLoadingPrice: isPriceServiceAvailable ? isLoadingPrice : false,
    isPriceServiceAvailable,
    productTotal,
    isPending,
    isDisabled: isUpdatingChildren,
    roomFormModel,
    callbacks: roomModel.core.handlers,
    hideControls: !roomModel.core.handlers.onEdit,
    isEditing: sidebarIsEditing,
    enterEditing: enterSidebarEditing,
    exitEditing: exitSidebarEditing,
    sidebarActiveTab,
    setSidebarActiveTab
  }
}
